import { NodeAxios } from '../api'
import type { 
    InterviewPack, 
    InterviewSession, 
    InterviewPackType,
    PackDefinition,
} from '@/types/campaigns'

export const interviewBotService = {
    /**
     * List available interview packs (global + org-scoped merged)
     */
    async listPacks(type?: InterviewPackType, commzone_code?: string): Promise<{ success: boolean; packs: InterviewPack[] }> {
        const params = new URLSearchParams()
        if (type) params.set('type', type)
        if (commzone_code) params.set('commzone_code', commzone_code)
        const query = params.toString() ? `?${params.toString()}` : ''
        const result = await NodeAxios.get(`/campaigns/interview/packs${query}`)
        return result.data || result
    },

    // ── Workspace Pack CRUD ──────────────────────────────────────────────────

    async listWorkspacePacks(type?: InterviewPackType): Promise<{ success: boolean; packs: InterviewPack[] }> {
        const query = type ? `?type=${type}` : ''
        const result = await NodeAxios.get(`/campaigns/interview/packs/workspace${query}`)
        return result.data || result
    },

    async createWorkspacePack(data: Partial<InterviewPack>): Promise<{ success: boolean; pack: InterviewPack }> {
        const result = await NodeAxios.post('/campaigns/interview/packs/workspace', data)
        return result.data || result
    },

    async copyPack(id: string): Promise<{ success: boolean; pack: InterviewPack }> {
        const result = await NodeAxios.post(`/campaigns/interview/packs/workspace/${id}/copy`, {})
        return result.data || result
    },

    async getWorkspacePack(id: string): Promise<{ success: boolean; pack: InterviewPack }> {
        const result = await NodeAxios.get(`/campaigns/interview/packs/workspace/${id}`)
        return result.data || result
    },

    async updateWorkspacePack(id: string, data: Partial<InterviewPack>): Promise<{ success: boolean; pack: InterviewPack }> {
        const result = await NodeAxios.patch(`/campaigns/interview/packs/workspace/${id}`, data)
        return result.data || result
    },

    async deleteWorkspacePack(id: string): Promise<{ success: boolean }> {
        const result = await NodeAxios.delete(`/campaigns/interview/packs/workspace/${id}`)
        return result.data || result
    },

    /**
     * Start a new interview session
     */
    async startSession(params: {
        packId: string
        subjectId: string
        subjectType: 'person' | 'company' | 'product'
        subjectName: string
        /** Optional variables to replace {{key}} in pack content (e.g. { hypothesis: "..." }). */
        variables?: Record<string, string>
    }): Promise<{ success: boolean; session: InterviewSession }> {
        const result = await NodeAxios.post('/campaigns/interview/sessions', params)
        return result.data || result
    },

    /**
     * Send a message and get AI follow-up.
     * In structured mode, also returns hoisted root-level fields for progress tracking.
     */
    async sendMessage(sessionId: string, message: string): Promise<{
        success: boolean
        session: InterviewSession
        nextText?: string
        isFollowUp?: boolean
        questionIndex?: number
        isComplete?: boolean
    }> {
        const result = await NodeAxios.post(`/campaigns/interview/sessions/${sessionId}/message`, { message })
        return result.data || result
    },

    /**
     * Admin: List ALL interview packs (no isActive filter)
     */
    async adminListPacks(): Promise<{ success: boolean; packs: InterviewPack[] }> {
        const result = await NodeAxios.get('/campaigns/admin/interview-packs')
        return result.data || result
    },

    /**
     * List interview sessions for a subject (Interview History)
     */
    async listSessions(subjectId: string): Promise<{ success: boolean; sessions: InterviewSession[] }> {
        const result = await NodeAxios.get(`/campaigns/interview/sessions?subjectId=${subjectId}`)
        return result.data || result
    },

    /**
     * Get a single session (full transcript)
     */
    async getSession(sessionId: string): Promise<{ success: boolean; session: InterviewSession }> {
        const result = await NodeAxios.get(`/campaigns/interview/sessions/${sessionId}`)
        return result.data || result
    },

    /**
     * Update session metadata (e.g. display_name for custom label)
     */
    async updateSession(sessionId: string, data: { display_name?: string }): Promise<{ success: boolean; session: InterviewSession }> {
        const result = await NodeAxios.patch(`/campaigns/interview/sessions/${sessionId}`, data)
        return result.data || result
    },

    /**
     * Finalize interview and create story(ies)
     */
    /**
     * Finalize interview — now enqueues a background harvest job.
     * Returns { queued: true } immediately; stories appear after processing.
     */
    async finalizeInterview(
        sessionId: string,
        _saveToDocument?: boolean,
        _extractionMode?: 'single' | 'multi',
        replace_mode?: boolean
    ): Promise<{ success: boolean; queued?: boolean; sessionId?: string; storyId?: string; storyIds?: string[] }> {
        const result = await NodeAxios.post(`/campaigns/interview/sessions/${sessionId}/finalize`, {
            replace_mode: replace_mode ?? false,
        })
        return result.data || result
    },

    /**
     * Reprocess session — enqueues a background harvest job.
     * Returns { queued: true } immediately; stories appear after processing.
     */
    async reprocessFromSession(
        sessionId: string,
        _strategy?: 'single' | 'multi',
        replace_mode?: boolean
    ): Promise<{ success: boolean; queued?: boolean; sessionId?: string; storyIds?: string[] }> {
        const result = await NodeAxios.post(`/campaigns/interview/sessions/${sessionId}/reprocess`, {
            replace_mode: replace_mode ?? false,
        })
        return result.data || result
    },

    /**
     * Reprocess a story with raw_content: create NEW story(ies). Always additive.
     */
    async reprocessFromStory(
        storyId: string,
        strategy?: 'single' | 'multi'
    ): Promise<{ success: boolean; storyIds: string[] }> {
        const result = await NodeAxios.post(`/campaigns/library/stories/${storyId}/reprocess`, {
            strategy: strategy || 'single',
        })
        return result.data || result
    },

    /**
     * Admin: Create a new interview pack
     */
    async adminCreatePack(data: Partial<InterviewPack>): Promise<{ success: boolean; pack: InterviewPack }> {
        const result = await NodeAxios.post('/campaigns/admin/interview-packs', data)
        return result.data || result
    },

    /**
     * Admin: Update an interview pack
     */
    async adminUpdatePack(packId: string, updates: Partial<InterviewPack>): Promise<{ success: boolean; pack: InterviewPack }> {
        const result = await NodeAxios.patch(`/campaigns/admin/interview-packs/${packId}`, updates)
        return result.data || result
    },

    /**
     * Admin: Delete an interview pack
     */
    async adminDeletePack(packId: string): Promise<{ success: boolean }> {
        const result = await NodeAxios.delete(`/campaigns/admin/interview-packs/${packId}`)
        return result.data || result
    },

    /**
     * Admin: Bulk import packs from a PackDefinition array.
     * If replaceAll is true, any global pack not in the array is deleted (hard sync).
     */
    async adminBulkImportPacks(packs: PackDefinition[], replaceAll = false): Promise<{
        success: boolean
        inserted: number
        updated: number
        deleted: number
        errors: string[]
    }> {
        const result = await NodeAxios.post('/campaigns/admin/interview-packs/bulk-import', { packs, replace_all: replaceAll })
        return result.data || result
    },

    /**
     * Admin: Export all global packs as a portable JSON array.
     * Triggers a file download in the browser.
     */
    async adminExportPacks(type?: string): Promise<void> {
        const url = type
            ? `/campaigns/admin/interview-packs/export?type=${encodeURIComponent(type)}`
            : '/campaigns/admin/interview-packs/export'
        const result = await NodeAxios.get(url, { responseType: 'blob' })
        const blob = new Blob([result.data], { type: 'application/json' })
        const href = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = href
        a.download = 'pack-library.json'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(href)
    },

    /**
     * Admin: Load the server-side pack-library.json seed file and upsert all packs.
     * Dev shortcut — no file upload needed.
     */
    async adminLoadSeedLibrary(): Promise<{
        success: boolean
        inserted: number
        updated: number
    }> {
        const result = await NodeAxios.post('/campaigns/admin/interview-packs/load-seed')
        return result.data || result
    },

    /**
     * Admin: Load the server-side bundle-library.json seed file and upsert all bundles.
     * Dev shortcut — no file upload needed.
     */
    async adminLoadSeedBundleLibrary(): Promise<{
        success: boolean
        inserted: number
        updated: number
        deleted: number
    }> {
        const result = await NodeAxios.post('/campaigns/admin/interview-bundles/load-seed')
        return result.data || result
    },

    /**
     * List ALL sessions for an org (for admin harvest panel)
     */
    async listAllSessions(status?: string): Promise<{ success: boolean; sessions: InterviewSession[] }> {
        const query = status ? `?status=${status}` : ''
        const result = await NodeAxios.get(`/campaigns/interview/sessions/all${query}`)
        return result.data || result
    },

    /**
     * Admin: Generate a full pack configuration from an idea
     */
    async adminGeneratePack(idea: string, type: InterviewPackType): Promise<{ success: boolean; pack: Partial<InterviewPack> }> {
        const result = await NodeAxios.post('/campaigns/admin/interview-packs/generate', { idea, type }, {
            timeout: 180000 // 3 minutes for AI (increased from 2m)
        })
        
        // NodeAxios returns response.data directly
        const data = result as any
        
        if (!data || data.success === false) {
            throw new Error(data?.error || 'Failed to generate pack')
        }
        
        return data
    },

    /**
     * Generate a magic link for a mobile/web interview session.
     * The link can be sent to the interviewee via email or QR code.
     */
    async generateMagicLink(params: {
        sessionId: string
        interviewee_name: string
        interviewee_email: string
        expires_in_hours?: number
    }): Promise<{ success: boolean; token: string; url: string; qrData: string; expiresAt: string }> {
        const { sessionId, ...body } = params
        const result = await NodeAxios.post(`/campaigns/interview/sessions/${sessionId}/magic-link`, body)
        return result.data || result
    },

    /**
     * Send an expert interview invitation with configurable duration and reuse count.
     */
    async sendInviteLink(params: {
        sessionId: string
        email: string
        duration_days?: number       // 1–30 days; default 2
        max_sessions?: number | null // null = unlimited reuse; default 1
    }): Promise<{ success: boolean; link: string; token: string; expiresAt: string; maxSessions: number | null; durationDays: number }> {
        const result = await NodeAxios.post(`/campaigns/interview/sessions/${params.sessionId}/invite`, {
            email: params.email,
            duration_days: params.duration_days,
            max_sessions: params.max_sessions,
        })
        return result.data || result
    },

    /** Admin: immediately close a session and revoke its link. */
    async killSession(sessionId: string): Promise<{ success: boolean; status: string }> {
        const result = await NodeAxios.post(`/campaigns/interview/sessions/${sessionId}/kill`, {})
        return result.data || result
    },

    /**
     * Admin: re-open a killed/expired session and issue a fresh 48-hour magic link.
     * Optionally override the interviewee name/email.
     */
    async reactivateSession(sessionId: string, params?: {
        interviewee_name?: string
        interviewee_email?: string
    }): Promise<{ success: boolean; token: string; url: string; qrData: string; expiresAt: string }> {
        const result = await NodeAxios.post(`/campaigns/interview/sessions/${sessionId}/reactivate`, params ?? {})
        return result.data || result
    },

    /**
     * Permanently delete an interview session and its magic link.
     */
    async deleteSession(sessionId: string): Promise<{ success: boolean }> {
        const result = await NodeAxios.delete(`/campaigns/interview/sessions/${sessionId}`)
        return result.data || result
    },

    /**
     * Compile active stories from a completed session into a Topic Document.
     * Idempotent — returns existing documentId if already compiled.
     */
    async compileDocument(sessionId: string): Promise<{ success: boolean; documentId: string }> {
        const result = await NodeAxios.post(`/campaigns/interview/sessions/${sessionId}/compile-document`, {})
        return result.data || result
    },

    // ── Workspace AI Pack Generation ─────────────────────────────────────────

    /**
     * Generate a full workspace pack from an idea — workspace-accessible (no admin required).
     * If packId is provided, the fields are upserted onto that existing pack.
     */
    async generateWorkspacePack(idea: string, type: InterviewPackType, packId?: string): Promise<{ success: boolean; pack: InterviewPack }> {
        const result = await NodeAxios.post('/campaigns/interview/packs/workspace/generate', { idea, type, packId }, {
            timeout: 180000,
        })
        return result.data || result
    },

    /**
     * Generate a single field (system_prompt, initial_message, questions, goals_outcomes)
     * for an existing workspace pack using the current pack context.
     */
    async generatePackField(
        packId: string,
        field: 'system_prompt' | 'initial_message' | 'questions' | 'goals_outcomes',
        packContext: Partial<InterviewPack>
    ): Promise<{ success: boolean; result: any }> {
        const result = await NodeAxios.post(`/campaigns/interview/packs/workspace/${packId}/generate-field`, { field, packContext }, {
            timeout: 60000,
        })
        return result.data || result
    },

    // ── Slide image helpers ───────────────────────────────────────────────────

    /**
     * Get (or lazily create) the Media Center folder for this pack's slide images.
     */
    async getSlidesFolder(packId: string): Promise<{ success: boolean; folder: { _id: string; name: string } }> {
        const result = await NodeAxios.get(`/campaigns/interview/packs/${packId}/slides-folder`)
        return result.data || result
    },

    /**
     * Upload an image file for a companion slide.
     * The file is stored in the pack's media folder (auto-created if needed)
     * and will appear in the Media Center.
     * Returns the url and thumbnail_url of the created document.
     */
    async uploadSlideImage(packId: string, file: File): Promise<{ url: string; thumbnail_url: string; document_id: string }> {
        const formData = new FormData()
        formData.append('image', file)
        const result = await NodeAxios.post(`/campaigns/interview/packs/${packId}/slides/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
        })
        const data = result.data || result
        // uploadMediaFiles returns an array of documents; grab the first
        const docs = data.documents ?? data.files ?? []
        if (docs.length > 0) {
            return { url: docs[0].url, thumbnail_url: docs[0].thumbnail_url ?? docs[0].url, document_id: docs[0]._id }
        }
        return data
    },
}

